// Contact form — Zod validation + EmailJS send.
// Env vars: VITE_EMAILJS_SERVICE_ID, VITE_EMAILJS_TEMPLATE_ID, VITE_EMAILJS_PUBLIC_KEY
// Em dev/preview sem env vars, mostra modo demo (sem enviar — log + status info).

import emailjs from "@emailjs/browser";
import { z } from "zod";

import { getLanguage, onLanguageChange, t } from "../lib/i18n";

const FIELDS = ["name", "email", "whatsapp", "message"] as const;
type FieldName = (typeof FIELDS)[number];

function getSchema() {
  // `t()` é avaliado eagerly aqui — `getSchema()` é re-chamado em cada
  // validate, então as mensagens refletem o idioma atual.
  // `.max()` defense-in-depth contra payloads gigantes (DoS, banda).
  return z.object({
    name: z
      .string()
      .trim()
      .min(2, { message: t("contact.error_name_min") })
      .max(120, { message: t("contact.error_name_max") }),
    email: z
      .string()
      .trim()
      .email({ message: t("contact.error_email_invalid") })
      .max(200, { message: t("contact.error_email_max") }),
    whatsapp: z
      .string()
      .trim()
      .optional()
      .refine((v) => !v || /^[\d\s()+\-.]{8,20}$/.test(v), {
        message: t("contact.error_whatsapp_invalid"),
      }),
    message: z
      .string()
      .trim()
      .min(10, { message: t("contact.error_message_min") })
      .max(5000, { message: t("contact.error_message_max") }),
  });
}

type FormState = "idle" | "loading" | "success" | "error";

export function initContactForm(): void {
  const formEl = document.getElementById("contactForm") as HTMLFormElement | null;
  if (!formEl) return;
  const form: HTMLFormElement = formEl;

  const submitBtn = form.querySelector<HTMLButtonElement>("#contactSubmit");
  const submitLabel = form.querySelector<HTMLElement>("[data-form-label]");
  const statusEl = form.querySelector<HTMLElement>("[data-form-status]");

  const inputs: Record<FieldName, HTMLInputElement | HTMLTextAreaElement> = {
    name: form.querySelector("#cf-name") as HTMLInputElement,
    email: form.querySelector("#cf-email") as HTMLInputElement,
    whatsapp: form.querySelector("#cf-whatsapp") as HTMLInputElement,
    message: form.querySelector("#cf-message") as HTMLTextAreaElement,
  };
  const errorEls: Record<FieldName, HTMLElement | null> = {
    name: form.querySelector('[data-error-for="name"]'),
    email: form.querySelector('[data-error-for="email"]'),
    whatsapp: form.querySelector('[data-error-for="whatsapp"]'),
    message: form.querySelector('[data-error-for="message"]'),
  };

  // EmailJS env config — sem prefixo VITE_ não chega ao bundle do cliente,
  // então essas devem ter VITE_.
  const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID as string | undefined;
  const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string | undefined;
  const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as string | undefined;
  const demoMode = !serviceId || !templateId || !publicKey;

  if (!demoMode && publicKey) {
    emailjs.init({ publicKey });
  }

  let state: FormState = "idle";
  let lastStatusKey: string = "";
  let lastStatusKind: "" | "success" | "error" | "info" = "";

  function submitLabelKey(s: FormState): string {
    if (s === "loading") return "contact.submit_loading";
    if (s === "success") return "contact.submit_success";
    return "contact.submit_idle";
  }

  function setState(next: FormState): void {
    state = next;
    if (submitBtn) submitBtn.disabled = next === "loading";
    if (submitLabel) submitLabel.textContent = t(submitLabelKey(next));
  }

  function setStatus(kind: "" | "success" | "error" | "info", msgKey: string): void {
    lastStatusKind = kind;
    lastStatusKey = msgKey;
    if (!statusEl) return;
    statusEl.classList.remove("success", "error", "info");
    if (!kind) {
      statusEl.textContent = "";
      return;
    }
    statusEl.classList.add(kind);
    statusEl.textContent = msgKey ? t(msgKey) : "";
  }

  function clearErrors(): void {
    for (const name of FIELDS) {
      const errEl = errorEls[name];
      if (errEl) errEl.textContent = "";
      inputs[name].removeAttribute("aria-invalid");
    }
  }

  function showErrors(issues: z.ZodIssue[]): void {
    clearErrors();
    for (const issue of issues) {
      const name = issue.path[0] as FieldName | undefined;
      if (!name) continue;
      const errEl = errorEls[name];
      if (errEl) errEl.textContent = issue.message;
      inputs[name].setAttribute("aria-invalid", "true");
    }
  }

  function readValues(): Record<FieldName, string> {
    return {
      name: inputs.name.value,
      email: inputs.email.value,
      whatsapp: inputs.whatsapp.value,
      message: inputs.message.value,
    };
  }

  async function onSubmit(ev: SubmitEvent): Promise<void> {
    ev.preventDefault();
    if (state === "loading") return;

    setStatus("", "");
    const values = readValues();
    const parsed = getSchema().safeParse(values);
    if (!parsed.success) {
      showErrors(parsed.error.issues);
      setStatus("error", "contact.status_validation");
      return;
    }
    clearErrors();

    if (demoMode) {
      // eslint-disable-next-line no-console
      console.info(
        "[contact-form] EmailJS env vars não configuradas — modo demo. Payload:",
        parsed.data,
      );
      setState("loading");
      window.setTimeout(() => {
        setState("success");
        setStatus("info", "contact.status_demo");
      }, 600);
      return;
    }

    setState("loading");
    try {
      await emailjs.send(serviceId!, templateId!, {
        from_name: parsed.data.name,
        reply_to: parsed.data.email,
        whatsapp: parsed.data.whatsapp ?? "",
        message: parsed.data.message,
        language: getLanguage(),
      });
      setState("success");
      setStatus("success", "contact.status_success");
      form.reset();
      window.setTimeout(() => setState("idle"), 4000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[contact-form] EmailJS error", err);
      setState("error");
      setStatus("error", "contact.status_error");
      window.setTimeout(() => setState("idle"), 4000);
    }
  }

  form.addEventListener("submit", (ev) => {
    void onSubmit(ev);
  });

  // Re-render i18n strings quando idioma muda — cobre TODOS os states do botão
  // E re-traduz o status message ativo + erros de validação atuais.
  onLanguageChange(() => {
    if (submitLabel) submitLabel.textContent = t(submitLabelKey(state));
    if (statusEl && lastStatusKind && lastStatusKey) {
      statusEl.textContent = t(lastStatusKey);
    }
    // Se houver erro de validação ativo, re-validar e re-mostrar mensagens traduzidas.
    if (lastStatusKind === "error") {
      const parsed = getSchema().safeParse(readValues());
      if (!parsed.success) showErrors(parsed.error.issues);
    }
  });
}
